/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import './BuyNowModal.scss';
import { useEffect, useRef, useState } from 'react';
import {
    Alert,
    BREAKPOINTS,
    Button,
    FeatureFlag,
    FlexRow,
    FlexCol,
    Icon,
    ModalActions,
    SecurityHelper,
    StyledModal,
    Heading,
    Stack,
    Spinner,
    useFlags,
    useMediaQuery,
} from 'sarsaparilla';
import axios from 'axios';
import OrderSummary from 'ui-cart/src/components/OrderSummary';
import { SavedOrNewCreditCardPicker } from 'ui-cart/src/components/digital-wallet/SavedOrNewCreditCardPicker';
import { getPRSCheckoutURL } from 'ui-cart/src/utils/cart_utils';
import * as constants from 'ui-cart/src/constants';
import { setBuyNowCheckoutMarker } from '../../utils/buyNowTracking';
import { useWafChallenge, postPrsPayment } from '../../utils/wafChallenge';
import BuyNowApplePay from './BuyNowApplePay';

const STEP_IDLE = 0;
const STEP_CONFIRMING_PAYMENT = 1;
const STEP_GENERATING_RECEIPT = 2;
const STEP_COMPLETING_ORDER = 3;
const STEP_COMPLETE = 4;
const STEP_ERROR = -1;

function CheckoutProgress({ step }) {
    return (
        <>
            <FlexRow alignItems="center" hasGutters={false} className="mb-2">
                <FlexCol xs={1} className="step-icon-wrap">
                    {step === STEP_CONFIRMING_PAYMENT ? (
                        <Spinner isCentered={false} size="md" />
                    ) : step > STEP_CONFIRMING_PAYMENT ? (
                        <Icon iconName="check-circle" size="lg" className="green-check" />
                    ) : null}
                </FlexCol>
                <FlexCol xs="auto">
                    <span
                        className={
                            step >= STEP_CONFIRMING_PAYMENT ? 'black-text' : 'gray-text'
                        }
                    >
                        {step <= STEP_CONFIRMING_PAYMENT
                            ? 'Confirming Payment'
                            : 'Payment Confirmed'}
                    </span>
                </FlexCol>
            </FlexRow>
            <FlexRow alignItems="center" hasGutters={false} className="mb-2">
                <FlexCol xs={1} className="step-icon-wrap">
                    {step === STEP_GENERATING_RECEIPT ? (
                        <Spinner isCentered={false} size="sm" />
                    ) : step > STEP_GENERATING_RECEIPT ? (
                        <Icon iconName="check-circle" size="lg" className="green-check" />
                    ) : null}
                </FlexCol>
                <FlexCol xs="auto">
                    <span
                        className={
                            step > STEP_CONFIRMING_PAYMENT ? 'black-text' : 'gray-text'
                        }
                    >
                        {step <= STEP_CONFIRMING_PAYMENT
                            ? 'Generate Receipt'
                            : step === STEP_GENERATING_RECEIPT
                              ? 'Generating Receipt'
                              : 'Receipt Generated'}
                    </span>
                </FlexCol>
            </FlexRow>
            <FlexRow alignItems="center" hasGutters={false} className="mb-2">
                <FlexCol xs={1} className="step-icon-wrap">
                    {step === STEP_COMPLETING_ORDER ? (
                        <Spinner isCentered={false} size="sm" />
                    ) : step === STEP_COMPLETE ? (
                        <Icon iconName="check-circle" size="lg" className="green-check" />
                    ) : null}
                </FlexCol>
                <FlexCol xs="auto">
                    <span
                        className={
                            step > STEP_GENERATING_RECEIPT ? 'black-text' : 'gray-text'
                        }
                    >
                        {step <= STEP_GENERATING_RECEIPT
                            ? 'Complete Order'
                            : step === STEP_COMPLETING_ORDER
                              ? 'Completing Order'
                              : 'Order Complete'}
                    </span>
                </FlexCol>
            </FlexRow>
            <FlexRow>
                <FlexCol className="mt-2">
                    <Alert heading="" type="info">
                        <p>
                            Please wait while we generate your order confirmation. Do not
                            close the browser, reload, or navigate away from the page to
                            ensure your reservation is successful,
                            <i> and to prevent being charged multiple times</i>.
                        </p>
                    </Alert>
                </FlexCol>
            </FlexRow>
        </>
    );
}

export default function BuyNowModal({
    isOpen,
    closeModal,
    onComplete,
    errors,
    cartData,
}) {
    const { enablePrsWafChallenge } = useFlags();
    const [paymentState, setPaymentState] = useState({});
    const [checkoutStep, setCheckoutStep] = useState(STEP_IDLE);
    const [paymentError, setPaymentError] = useState(null);

    useWafChallenge(enablePrsWafChallenge);

    useEffect(() => {
        if (isOpen) {
            setPaymentState({});
            setCheckoutStep(STEP_IDLE);
            setPaymentError(null);
        }
    }, [isOpen]);

    const closeModalRef = useRef(closeModal);
    closeModalRef.current = closeModal;
    const checkoutStepRef = useRef(checkoutStep);
    checkoutStepRef.current = checkoutStep;
    const isMountedRef = useRef(true);

    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
        };
    }, []);

    const cartExpiration = cartData?.expiration;
    useEffect(() => {
        if (!isOpen || !cartExpiration) return;

        const timeRemaining = new Date(cartExpiration).getTime() - Date.now();
        if (timeRemaining <= 0 && checkoutStepRef.current === STEP_IDLE) {
            closeModalRef.current();
            return;
        }

        const timer = setTimeout(() => {
            if (checkoutStepRef.current === STEP_IDLE) {
                closeModalRef.current();
            }
        }, timeRemaining);
        return () => clearTimeout(timer);
    }, [isOpen, cartExpiration]);

    useEffect(() => {
        if (!isOpen) return;

        const handleVisibilityChange = () => {
            if (document.hidden && checkoutStepRef.current === STEP_IDLE) {
                closeModalRef.current();
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);
        return () =>
            document.removeEventListener('visibilitychange', handleVisibilityChange);
    }, [isOpen]);

    const updatePaymentState = (state) => {
        setPaymentState((prev) => ({ ...prev, ...state }));
    };

    const handleCheckout = async () => {
        if (!paymentState.selectedSavedCardToken) {
            return;
        }

        setCheckoutStep(STEP_CONFIRMING_PAYMENT);
        setPaymentError(null);

        let checkoutID;
        let checkoutToken;

        try {
            const headers = { Authorization: SecurityHelper.getAuthHeader() };
            const checkoutResponse = await axios.post(
                `${process.env.API}/cart/checkout`,
                { is_buy_now: true },
                { headers }
            );
            checkoutID = checkoutResponse.data.checkout_id;
            checkoutToken = checkoutResponse.data.token;

            setBuyNowCheckoutMarker({ checkoutId: checkoutID });
        } catch {
            setCheckoutStep(STEP_ERROR);
            setPaymentError('Unable to start checkout. Please try again.');
            return;
        }

        try {
            const paymentURL = getPRSCheckoutURL();

            const request = {
                checkout_id: checkoutID,
                checkout_token: checkoutToken,
                payment_data: {
                    payment_type: constants.PAYMENT_TYPE_WALLET,
                    prs_wallet_data: {
                        wallet_token: paymentState.selectedSavedCardToken,
                    },
                },
            };

            await postPrsPayment(`${paymentURL}/${checkoutID}`, request, {
                enableWaf: enablePrsWafChallenge,
            });

            setCheckoutStep(STEP_GENERATING_RECEIPT);

            const pollCheckoutStatus = async () => {
                const headers = { Authorization: SecurityHelper.getAuthHeader() };
                const startTime = Date.now();
                const maxWait = 60000;
                let pollCount = 0;

                while (Date.now() - startTime < maxWait) {
                    if (!isMountedRef.current) return;
                    pollCount += 1;
                    try {
                        const statusResponse = await axios.get(
                            `${process.env.API}/cart/direct_checkouts/status/${checkoutID}`,
                            { headers }
                        );
                        if (statusResponse?.data?.status === 'PAID') {
                            if (!isMountedRef.current) return;
                            setCheckoutStep(STEP_COMPLETING_ORDER);
                            setTimeout(() => {
                                setCheckoutStep(STEP_COMPLETE);
                                setTimeout(() => {
                                    window.location.assign(
                                        `/cart?finalize=${checkoutID}`
                                    );
                                }, 1000);
                            }, 1000);
                            return;
                        }
                    } catch {
                        // continue polling
                    }
                    const delay = Math.min(5000 * pollCount, 10000);
                    await new Promise((resolve) => setTimeout(resolve, delay));
                }

                if (!isMountedRef.current) return;
                window.location.assign(`/cart?finalize=${checkoutID}&pending=true`);
            };

            pollCheckoutStatus();
        } catch {
            setCheckoutStep(STEP_ERROR);
            setPaymentError(
                'Payment failed. Please try again or select a different card.'
            );
        }
    };

    const isResponsive = useMediaQuery(`(max-width: ${BREAKPOINTS.md - 1}px)`);
    const isProcessing = checkoutStep > STEP_IDLE;

    return (
        <StyledModal
            isOpen={isOpen}
            onRequestClose={!isProcessing ? closeModal : undefined}
            shouldShowCloseButton={!isProcessing}
            heading={isProcessing ? '' : 'Order Summary'}
            contentLabel="Buy Now"
            size="lg"
            className="buy-now-modal"
        >
            {isProcessing ? (
                <>
                    {checkoutStep === STEP_ERROR ? (
                        <>
                            <Alert type="error" heading="Payment problem">
                                <p>{paymentError}</p>
                            </Alert>
                            <ModalActions>
                                <Button
                                    appearance="tertiary"
                                    onClick={() => {
                                        setCheckoutStep(STEP_IDLE);
                                        setPaymentError(null);
                                    }}
                                >
                                    Try Again
                                </Button>
                            </ModalActions>
                        </>
                    ) : (
                        <CheckoutProgress step={checkoutStep} />
                    )}
                </>
            ) : errors?.length > 0 ? (
                <Alert type="error" heading="Unable to complete purchase">
                    <ul>
                        {errors.map((error, i) => (
                            <li key={i}>{error.message}</li>
                        ))}
                    </ul>
                </Alert>
            ) : (
                <Stack>
                    {cartData && (
                        <OrderSummary
                            simple
                            reservations={cartData.reservations}
                            subtotal={cartData.subtotal}
                            tax={cartData.tax}
                            total={cartData.total}
                            discount={cartData.discount}
                        />
                    )}

                    <div>
                        <Heading headingLevel={3} appearance="h6">
                            Payment Method
                        </Heading>

                        <Stack>
                            <FeatureFlag flag="cartBuyNowApplePay">
                                <BuyNowApplePay
                                    cartData={cartData}
                                    onComplete={onComplete}
                                />
                            </FeatureFlag>
                            <SavedOrNewCreditCardPicker
                                showPurchaseGiftCard={false}
                                updateParentState={updatePaymentState}
                                storedOnly
                                simple
                                showBorder
                            />
                        </Stack>
                    </div>

                    <FlexRow alignItems="center">
                        <FlexCol xs={6}>
                            <Button
                                appearance="tertiary"
                                onClick={closeModal}
                                shouldFitContainer
                                gaTrackingId="649764456261"
                            >
                                Continue Shopping
                            </Button>
                        </FlexCol>
                        <FlexCol xs={6}>
                            <Button
                                onClick={handleCheckout}
                                isDisabled={!paymentState.selectedSavedCardToken}
                                shouldFitContainer
                                gaTrackingId="147894456453"
                            >
                                Confirm
                            </Button>
                        </FlexCol>
                    </FlexRow>

                    {isResponsive && (
                        <Button
                            appearance="tertiary"
                            onClick={closeModal}
                            shouldFitContainer
                            iconBeforeElement={<Icon iconName="arrow-prev" />}
                        >
                            Go Back
                        </Button>
                    )}
                </Stack>
            )}
        </StyledModal>
    );
}
